/**
 * API Base URL configuration for different environments
 * Shared across cggenai-ui and cg-agentic-ui packages
 */

/**
 * Environment-specific API Gateway endpoints
 */
export const API_BASE_URLS = {
  DEV: "https://ar2uv78oq3.execute-api.eu-west-1.amazonaws.com/dev",
  UAT: "https://hd8f04xrbc.execute-api.eu-west-1.amazonaws.com/uat",
  PROD: "https://7by47hhrnk.execute-api.eu-west-1.amazonaws.com/production",
} as const;

/**
 * Environment name constants for deployment detection
 */
export const ENVIRONMENT_NAMES = {
  DEVELOPMENT: "development",
  DEV: "dev",
  UAT: "uat",
  PRODUCTION: "production",
} as const;

export type EnvironmentName =
  (typeof ENVIRONMENT_NAMES)[keyof typeof ENVIRONMENT_NAMES];

/**
 * Get the current environment mode from Vite
 * This function is designed to work with Vite's import.meta.env.MODE
 */
export function getEnvironmentMode(): string {
  // In browser context with Vite
  if (typeof import.meta !== "undefined" && import.meta.env?.MODE) {
    return import.meta.env.MODE;
  }
  // Fallback for SSR or non-Vite environments
  return ENVIRONMENT_NAMES.DEVELOPMENT;
}

/**
 * Get the API base URL based on the current environment
 * @returns The API base URL for the current environment
 *
 * Environment mapping:
 * - 'development' -> '' (empty string for local proxy)
 * - 'dev' -> DEV API Gateway
 * - 'uat' -> UAT API Gateway
 * - 'production' -> PROD API Gateway
 */
export function getAPIBaseURL(): string {
  const mode = getEnvironmentMode();

  switch (mode) {
    case ENVIRONMENT_NAMES.DEVELOPMENT:
      // Local development - use proxy or empty string for relative URLs
      return "";
    case ENVIRONMENT_NAMES.DEV:
      return API_BASE_URLS.DEV;
    case ENVIRONMENT_NAMES.UAT:
      return API_BASE_URLS.UAT;
    case ENVIRONMENT_NAMES.PRODUCTION:
      return API_BASE_URLS.PROD;
    default:
      // Fallback to dev for unknown environments
      return API_BASE_URLS.DEV;
  }
}

/**
 * Get the full URL for an API route
 * @param route - The API route (e.g., '/api/v1/users')
 * @returns The full URL including the base URL
 */
export function getFullURL(route: string): string {
  const baseURL = getAPIBaseURL();
  // If baseURL is empty (local dev), return the route as-is
  if (!baseURL) return route;
  // Ensure we don't double-up slashes
  return `${baseURL}${route.startsWith("/") ? route : `/${route}`}`;
}

/**
 * Check if the current environment is local development
 */
export function isLocalDevelopment(): boolean {
  return getEnvironmentMode() === ENVIRONMENT_NAMES.DEVELOPMENT;
}

/**
 * Check if the current environment is production
 */
export function isProduction(): boolean {
  return getEnvironmentMode() === ENVIRONMENT_NAMES.PRODUCTION;
}
